import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Youtube } from "lucide-react";
import { useLang, STR, loc } from "@/i18n/translations";
import { ArtistPhoto, srcSetOf } from "@/components/ArtistPhoto";
import { SocialLinks } from "@/components/SocialIcons";
import api from "@/lib/api";

const ytId = (v) => (v.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([A-Za-z0-9_-]{11})/) || [])[1] || (/^[A-Za-z0-9_-]{11}$/.test(v) ? v : null);

export default function ArtistDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [artist, setArtist] = useState(null);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    setArtist(null);
    api.get(`/artists/${slug}`).then((r) => setArtist(r.data)).catch(() => setArtist(false));
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, [slug]);

  if (artist === null) return <div className="pt-40 pb-40 text-center text-cream/50">{t(STR.common.loading)}</div>;
  if (artist === false) return (
    <div className="pt-40 pb-40 text-center text-cream/60">
      <p className="font-serif text-3xl mb-6">{t({ fr: "Artiste introuvable", en: "Artist not found" })}</p>
      <Link to="/artistes" className="btn-outline-gold rounded-full px-6 py-3" data-testid="artist-404-back">{t(STR.common.backToArtists)}</Link>
    </div>
  );

  const bio = lang === "fr" ? artist.bio_fr : artist.bio_en;
  const artistEvents = events.filter((e) => (e.artist || "").includes(artist.name));
  const videoIds = (artist.videos || []).map(ytId).filter(Boolean);

  return (
    <div className="pt-24 pb-24">
      <div className="relative h-[50vh] overflow-hidden">
        <ArtistPhoto src={artist.image} alt={artist.name} position={artist.image_position} sizes="100vw" loading="eager" className="opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/10" />
        <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black/95 via-black/70 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 max-w-7xl mx-auto px-5 lg:px-8 pb-10 text-shadow-strong">
          <Link to="/artistes" className="flex w-fit items-center gap-2 text-white hover:text-gold mb-4" data-testid="back-to-artists">
            <ArrowLeft className="w-4 h-4" /> {t(STR.common.backToArtists)}
          </Link>
          <h1 className="font-serif text-5xl lg:text-7xl font-bold text-white" data-testid="artist-hero-name">{artist.name}</h1>
          <p className="mt-3 inline-block text-gold-light text-base lg:text-lg font-semibold bg-black/60 backdrop-blur-sm rounded px-3 py-1.5" data-testid="artist-hero-genre">{loc(artist, "genre", lang)}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-3 gap-12 mt-14">
        <div className="lg:col-span-2 space-y-12">
          <div>
            <h2 className="eyebrow mb-4">{t(STR.common.biography)}</h2>
            <p className="text-cream/70 leading-relaxed text-lg">{bio}</p>
          </div>

          {artist.photos && artist.photos.length > 0 && (
            <div>
              <h2 className="eyebrow mb-4">{t({ fr: "Galerie", en: "Gallery" })}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="artist-gallery">
                {artist.photos.map((p, i) => {
                  const credit = artist.photo_credits?.[p];
                  return (
                    <figure key={p} className="relative rounded-xl overflow-hidden border border-gold/15 group bg-black" data-testid={`artist-gallery-item-${i}`}>
                      <a href={p} target="_blank" rel="noopener noreferrer" className="block aspect-square overflow-hidden">
                        <img src={p} srcSet={srcSetOf(p)} sizes="(max-width: 640px) 50vw, 25vw" loading="lazy" alt={`${artist.name} ${i + 1}`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      </a>
                      {credit && <figcaption className="absolute bottom-0 inset-x-0 px-2.5 py-1.5 text-[11px] text-white bg-black/75 pointer-events-none" data-testid={`artist-photo-credit-${i}`}>{t({ fr: "Photo : ", en: "Photo: " })}{credit}</figcaption>}
                    </figure>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <h2 className="eyebrow mb-4">{t(STR.common.videos)}</h2>
            {videoIds.length > 0 ? (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="artist-videos">
                {videoIds.map((id, i) => (
                  <div key={id} className="aspect-video rounded-xl overflow-hidden border border-gold/20 bg-black">
                    <iframe src={`https://www.youtube-nocookie.com/embed/${id}`} title={`${artist.name} ${t({ fr: "vidéo", en: "video" })} ${i + 1}`} loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" className="w-full h-full" allowFullScreen data-testid={`artist-video-${i}`} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-cream/50 italic">{t(STR.common.noVideos)}</p>
            )}
            {artist.socials?.youtube && (
              <a href={artist.socials.youtube} target="_blank" rel="noopener noreferrer" data-testid="artist-youtube-channel-btn"
                 className="mt-4 inline-flex items-center gap-2 text-sm text-gold hover:text-gold-light">
                <Youtube className="w-4 h-4" /> {t({ fr: "Voir toute la chaîne YouTube", en: "See the full YouTube channel" })}
              </a>
            )}
          </div>

          <div>
            <h2 className="eyebrow mb-4">{t(STR.common.tourDates)}</h2>
            {artistEvents.length > 0 ? (
              <div className="space-y-3">
                {artistEvents.map((e) => (
                  <div key={e.id} className="card-dark rounded-lg p-4 flex items-center gap-4">
                    <CalendarDays className="w-5 h-5 text-gold" />
                    <div>
                      <p className="font-medium">{loc(e, "title", lang)}</p>
                      <p className="text-sm text-cream/60">{e.date} · {loc(e, "city", lang)} · {loc(e, "venue", lang)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-cream/50 italic">{t(STR.common.noDates)}</p>
            )}
          </div>
        </div>

        <aside className="space-y-6">
          <div className="card-dark rounded-2xl p-6 sticky top-28">
            <h3 className="font-serif text-2xl mb-1">{artist.name}</h3>
            <p className="text-sm text-cream/60">{t(STR.common.genre)} : {loc(artist, "genre", lang)}</p>
            {artist.origin && <p className="text-sm text-gold/80 mb-5">{loc(artist, "origin", lang)}</p>}
            {!artist.origin && <div className="mb-5" />}
            <p className="eyebrow mb-3">{t({ fr: "Ses réseaux", en: "Socials" })}</p>
            <SocialLinks links={artist.socials} owner={artist.name} prefix="artist-social" className="mb-6" showLabel
              itemClass="inline-flex items-center gap-2 px-3 h-10 rounded-lg border border-gold/25 text-sm text-cream/80 hover:text-gold hover:border-gold transition-colors" />
            <Link to={`/booking?artist=${encodeURIComponent(artist.name)}`} data-testid="book-artist-btn" className="btn-gold rounded-full w-full py-3.5 text-center block">
              {t(STR.common.bookThisArtist)}
            </Link>
            {artist.epk && (
              <a href={artist.epk} target="_blank" rel="noopener noreferrer" data-testid="artist-epk-btn" className="btn-outline-gold rounded-full w-full py-3 text-center block mt-3">
                {t({ fr: "Demander un EPK", en: "Request EPK" })}
              </a>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
